// src/intervenciones/IntervencionPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { IniciarIntervencion } from "../intervenciones/iniciarIntervencion";
import { FinalizarIntervencion } from "../intervenciones/finalizarIntervencion";
import useAuth from "../hooks/useAuth";
import { ticajeStore } from "../data/ticajeStore";

// --- Toast fijo, posicionado justo debajo de .header + .navi ---
const Toast = ({ type = "info", children }) => {
  const [top, setTop] = useState(90);
  useEffect(() => {
    const calcTop = () => {
      const header = document.querySelector(".header");
      const navi = document.querySelector(".navi");
      const h = (header?.offsetHeight || 0) + (navi?.offsetHeight || 0);
      setTop(h > 0 ? h + 12 : 90);
    };
    calcTop();
    window.addEventListener("resize", calcTop);
    return () => window.removeEventListener("resize", calcTop);
  }, []);
  const base = {
    position: "fixed",
    top,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 9999,
    padding: "12px 18px",
    borderRadius: 8,
    fontWeight: 600,
    boxShadow: "0 8px 24px rgba(0,0,0,.12)",
    pointerEvents: "none",
    maxWidth: "90vw",
    textAlign: "center",
  };
  const variants = {
    ok:    { background: "#e7f8ed", color: "#0f5132", border: "1px solid #b6f0c6" },
    error: { background: "#fdeceb", color: "#842029", border: "1px solid #f5c2c7" },
    info:  { background: "#e8f6ff", color: "#084298", border: "1px solid #b6d9ff" },
  };
  return <div style={{ ...base, ...(variants[type] || {}) }}>{children}</div>;
};

export const IntervencionPage = () => {
  const { auth } = useAuth();

  const [ticajesEnProceso, setTicajesEnProceso] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const [toast, setToast] = useState(null);           // { type, text }
  const [forcedView, setForcedView] = useState(null); // 'start' | 'finish' | null
  const [authExpired, setAuthExpired] = useState(false);
  const [hasPendingLocal, setHasPendingLocal] = useState(false);

  const toastDurationMs = 4000;
  const isFetchingRef = useRef(false);
  const unsubRef = useRef(null);

  // --- Bootstrap de la store y suscripción a cambios ---
  useEffect(() => {
    ticajeStore.bootstrap().catch(() => {});
    unsubRef.current = ticajeStore.subscribe(() => {
      // Igual que con jornada: si hay forcedView (toast activo), no refrescamos la lista
      if (!forcedView) fetchTicajesEnProceso({ refresh: true });
    });
    return () => {
      if (unsubRef.current) unsubRef.current();
    };
  }, [forcedView]);

  const fetchTicajesEnProceso = async ({ refresh = false } = {}) => {
    try {
      if (forcedView) return;
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      if (!refresh) setInitialLoading(true);

      // La store decide online/offline y ya sabe del usuario
      const active = ticajeStore.getActive(); // objeto o null
      const local  = ticajeStore.getLocal();  // objeto local o null

      const pending = Boolean(local?.pendingFinish || local?.pendingCancel);

      if (!active) {
        // Preferimos mostrar la local si existe y NO está marcada como pending
        if (local && !(local.pendingFinish || local.pendingCancel)) {
          setTicajesEnProceso([local]);
        } else {
          setTicajesEnProceso([]);
        }
        setHasPendingLocal(pending);
        setAuthExpired(false);
      } else {
        // Oculta intervenciones marcadas como pending (no deben mostrarse como finalizables)
        const visible = (active.pendingFinish || active.pendingCancel) ? [] : [active];
        setTicajesEnProceso(visible);
        setHasPendingLocal(pending);
        setAuthExpired(false);
      }
    } catch (e) {
      // Si en algún momento la store propaga AUTH_EXPIRED, lo tratamos igual que JornadaPage
      if (e?.code === "AUTH_EXPIRED") {
        setAuthExpired(true);
        setTicajesEnProceso([]);
      }
    } finally {
      isFetchingRef.current = false;
      if (!refresh) setInitialLoading(false);
    }
  };

  // Carga inicial al tener usuario
  useEffect(() => {
    if (auth?.id) fetchTicajesEnProceso({ refresh: false });
  }, [auth?.id]);

  const hayActiva = ticajesEnProceso.length > 0;

  // Fecha robusta para ordenar (distintos nombres posibles)
  const getStartDate = (t) =>
    new Date(
      t?.inicio ??
      t?.fecha_inicio ??
      t?.created_at ??
      t?.inicio_iso ??
      0
    );

  const ticajeActual = useMemo(() => {
    if (!hayActiva) return null;
    return [...ticajesEnProceso].sort((a, b) => getStartDate(b) - getStartDate(a))[0];
  }, [hayActiva, ticajesEnProceso]);

  const toastThenRefresh = ({ type, text, keepView, ms = toastDurationMs }) => {
    setToast({ type, text });
    setForcedView(keepView);
    window.setTimeout(async () => {
      setToast(null);
      setForcedView(null);
      await fetchTicajesEnProceso({ refresh: true });
    }, ms);
  };

  if (initialLoading) return <p>Cargando datos...</p>;

  const uiLocked = Boolean(forcedView);
  const showIniciar   = !authExpired && (forcedView ? forcedView === "start"  : !hayActiva);
  const showFinalizar = !authExpired && (forcedView ? forcedView === "finish" :  hayActiva);

  const safeRefresh = () => {
    if (!forcedView) fetchTicajesEnProceso({ refresh: true });
  };

  return (
    <div className="ticaje-frame">
      {toast && <Toast type={toast.type}>{toast.text}</Toast>}

      {hasPendingLocal && (
        <div
          style={{
            background: "#e8f6ff",
            border: "1px solid #b6d9ff",
            color: "#084298",
            padding: "10px 12px",
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          <b>Sin conexión:</b> tu cierre/cancelación de la intervención se enviará automáticamente
          al recuperar la red.
        </div>
      )}

      {authExpired && (
        <div
          style={{
            background: "#fff3cd",
            border: "1px solid #ffe69c",
            color: "#664d03",
            padding: "10px 12px",
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          <b>Token inválido o expirado.</b> Por seguridad debes iniciar sesión de nuevo.
        </div>
      )}

      {showIniciar && (
        <IniciarIntervencion
          uiLocked={uiLocked}
          onTicajeActualizado={safeRefresh}
          onSuccess={(msg) =>
            toastThenRefresh({
              type: "ok",
              text: msg || "Intervención iniciada correctamente.",
              // tras iniciar, normalmente iremos a Finalizar, pero como
              // la intervención puede “nacer” offline y tardar en sincronizar,
              // mantenemos la vista en "start" igual que con jornadas
              keepView: "start",
            })
          }
          onError={(msg) =>
            toastThenRefresh({
              type: "error",
              text: msg || "No se pudo iniciar la intervención.",
              keepView: "start",
            })
          }
        />
      )}

      {showFinalizar && (
        <FinalizarIntervencion
          uiLocked={uiLocked}
          ticaje={ticajeActual}
          onTicajeActualizado={safeRefresh}
          onSuccess={(msg) =>
            toastThenRefresh({
              type: "ok",
              text: msg || "Intervención finalizada correctamente.",
              keepView: "start", // tras cerrar/cancelar volvemos a Iniciar
            })
          }
          onError={(msg) =>
            toastThenRefresh({
              type: "error",
              text: msg || "No se pudo finalizar la intervención.",
              keepView: "finish",
            })
          }
        />
      )}
    </div>
  );
};

export default IntervencionPage;
